"use client";

import { produitsMoment, collectionPlatinum, formatPrice } from "@/lib/data";
import { useCart, useUI } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Star, ShoppingBag } from "lucide-react";
import Image from "next/image";

export function ProduitsMoment() {
  const addItem = useCart((s) => s.addItem);
  const openBoutique = useUI((s) => s.openBoutique);

  return (
    <section id="nouveautes" className="py-16 sm:py-20 bg-[#F5FBF7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div className="max-w-2xl">
            <p className="text-xs font-bold tracking-[0.3em] text-[#006B3F] uppercase mb-3">
              Les Dernières Nouveautés Prisées par Tous
            </p>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">
              Produits du{" "}
              <span className="bg-gradient-to-r from-[#006B3F] to-[#008751] bg-clip-text text-transparent">
                Moment
              </span>
            </h2>
            <div className="senegal-bar mt-5" />
            <p className="mt-3 text-gray-500 leading-relaxed">
              Les meilleurs, les dernières nouveautés les plus prisées. Du Pack Pro complet au
              Gaming, en passant par l&apos;électroménager et la maison connectée.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => openBoutique("tout")}
            className="border-[#006B3F]/30 text-[#006B3F] hover:bg-[#006B3F] hover:text-white shrink-0"
          >
            Voir tout
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {produitsMoment.map((p) => {
            const discount = p.oldPrice ? Math.round((1 - p.price / p.oldPrice) * 100) : 0;
            return (
              <div
                key={p.id}
                className="group bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-1.5 flex flex-col"
              >
                <div className="relative aspect-square overflow-hidden bg-gray-50">
                  <Image
                    src={p.image}
                    alt={p.name}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-700"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                  />
                  {p.badge && (
                    <Badge className="absolute top-3 left-3 bg-gradient-to-r from-[#006B3F] to-[#008751] text-white border-0 text-[10px] font-black tracking-wider">
                      {p.badge}
                    </Badge>
                  )}
                  {discount > 0 && (
                    <span className="absolute top-3 right-3 px-2 py-1 rounded-full bg-[#CE1126] text-white text-[11px] font-black">
                      -{discount}%
                    </span>
                  )}
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <p className="text-[10px] font-bold tracking-wider text-gray-400 uppercase">{p.category}</p>
                  <h3 className="font-bold text-gray-900 mt-1 leading-snug">{p.name}</h3>
                  <p className="text-xs text-gray-500 mt-1.5 leading-relaxed line-clamp-2 flex-1">
                    {p.description}
                  </p>
                  <div className="mt-4 flex items-end justify-between gap-2">
                    <div>
                      <p className="text-lg font-black text-[#006B3F]">
                        {formatPrice(p.price)}{" "}
                        <span className="text-[10px] font-bold text-gray-400">FCFA</span>
                      </p>
                      {p.oldPrice && (
                        <p className="text-xs text-gray-400 line-through">{formatPrice(p.oldPrice)} FCFA</p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={() =>
                        addItem({ id: p.id, name: p.name, price: p.price, image: p.image })
                      }
                      className="bg-gradient-to-r from-[#006B3F] to-[#008751] hover:shadow-lg text-white shrink-0"
                    >
                      <ShoppingBag className="w-3.5 h-3.5 mr-1" /> Acheter
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function Platinum() {
  const addItem = useCart((s) => s.addItem);

  return (
    <section id="platinum" className="py-16 sm:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-xs font-bold tracking-[0.3em] text-[#B8860B] uppercase mb-3">
            Collection Prestige
          </p>
          <h2 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">
            Collection{" "}
            <span className="bg-gradient-to-r from-[#B8860B] via-[#DAA520] to-[#FFD700] bg-clip-text text-transparent">
              Platinum
            </span>
          </h2>
          <p className="mt-4 text-gray-500 leading-relaxed">
            L&apos;élite de la technologie. Produits d&apos;édition limitée, garanties premium et
            service concierge blanc.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {["Garantie 3 Ans extension offerte", "Livraison Concierge Blanche", "Paiement 6x sans frais", "Cadeaux VIP luxury"].map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#FDF6E3] to-[#FBF0D5] border border-[#DAA520]/30 text-xs font-semibold text-[#8B6914]"
              >
                <Star className="w-3 h-3 fill-[#DAA520] text-[#DAA520]" /> {t}
              </span>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {collectionPlatinum.map((p) => {
      const discount = p.oldPrice ? Math.round((1 - p.price / p.oldPrice) * 100) : 0;
      return (
        <div
          key={p.id}
          className="group bg-gradient-to-b from-white to-[#FDF9EE] rounded-3xl overflow-hidden border border-[#DAA520]/20 shadow-sm hover:shadow-2xl hover:border-[#DAA520]/50 transition-all duration-500 hover:-translate-y-1.5"
        >
          <div className="relative aspect-[4/3] overflow-hidden bg-gray-50">
            <Image
              src={p.image}
              alt={p.name}
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-700"
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            />
            <span className="absolute top-3 left-3 px-3 py-1 rounded-full text-[10px] font-black tracking-widest bg-gradient-to-r from-[#B8860B] to-[#DAA520] text-white shadow-lg">
              {p.badge}
            </span>
            {discount > 0 && (
              <span className="absolute top-3 right-3 px-2 py-1 rounded-full bg-[#CE1126] text-white text-[11px] font-black">
                -{discount}%
              </span>
            )}
          </div>
          <div className="p-5">
            <p className="text-[10px] font-bold tracking-wider text-[#B8860B] uppercase">{p.category}</p>
            <h3 className="font-bold text-gray-900 mt-1">{p.name}</h3>
            {p.rating && (
              <div className="flex items-center gap-1.5 mt-2">
                <div className="flex">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`w-3.5 h-3.5 ${
                        i < Math.round(p.rating!)
                          ? "fill-[#FFD700] text-[#FFD700]"
                          : "fill-gray-200 text-gray-200"
                      }`}
                    />
                  ))}
                </div>
                <span className="text-xs font-bold text-gray-700">{p.rating}</span>
                <span className="text-xs text-gray-400">({p.reviews})</span>
              </div>
            )}
            <div className="mt-3 flex items-end justify-between gap-2">
              <div>
                <p className="text-lg font-black text-gray-900">
                  {formatPrice(p.price)}{" "}
                  <span className="text-[10px] font-bold text-gray-400">FCFA</span>
                </p>
                {p.oldPrice && (
                  <p className="text-xs text-gray-400 line-through">{formatPrice(p.oldPrice)} FCFA</p>
                )}
              </div>
              <Button
                size="sm"
                onClick={() =>
                  addItem({ id: p.id, name: p.name, price: p.price, image: p.image })
                }
                className="bg-gradient-to-r from-[#B8860B] to-[#DAA520] hover:shadow-lg text-white shrink-0"
              >
                Ajouter au panier
              </Button>
            </div>
          </div>
        </div>
      );
      })}
        </div>
      </div>
    </section>
  );
}
